import { AfterViewInit, Component, ElementRef, inject, PLATFORM_ID, signal, ViewChild, OnDestroy, effect } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { EventService } from '../../services/event.service';
import { PixelService } from '../../services/pixel.service';
import { isPlatformBrowser } from '@angular/common';
import { ExitIntentComponent } from '../../modals/exit-intent/exit-intent.component';
import { VideoModalComponent } from '../../modals/video-modal/video-modal.component';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { fromEvent, merge, timer, Subject } from 'rxjs';
import { switchMap, takeUntil, filter, startWith } from 'rxjs/operators';
import { PresaleDataService } from '../../services/connection-services/data.service';
import { EvmPurchseService } from '../../services/evm-services/evm-purchase.service';
import { SwapStatus } from '../../shared/constants/app-enums';

interface HeroActivity {
  prdxAmount: string;
  usdAmount: string;
  walletAddress: string;
  timestamp: string;
}

declare global {
  interface Window {
    initRingsAnimation: (container?: HTMLElement) => { destroy: () => void };
  }
}

@Component({
  selector: 'app-banner',
  templateUrl: './banner.component.html',
  styleUrl: './banner.component.scss',
  standalone: false,
})
export class BannerComponent implements AfterViewInit, OnDestroy {
  @ViewChild('ringsCanvas') ringsCanvas!: ElementRef<HTMLDivElement>;
  private ringsAnimationDestroy?: () => void;
  private readonly _platformId = inject(PLATFORM_ID);
  private status = SwapStatus.not_started
  lang: string = '';
  modalRef?: BsModalRef;

  // RxJS Subject to handle stopping the event listeners
  private stopListeners$ = new Subject<void>();
  private popupShown = false;

  // Placeholder activity data — wire to live feed from PresaleDataService when available
  readonly activity = signal<HeroActivity>({
    prdxAmount: '330,846',
    usdAmount: '$1,243.984',
    walletAddress: '0x153....569e',
    timestamp: '2nd Sep, 09:48 AU',
  });

  private readonly translate = inject(TranslateService);
  // private readonly pixelService = inject(PixelService);
  private readonly eventService = inject(EventService);
  private readonly purchaseService = inject(EvmPurchseService);
  private readonly modalService = inject(BsModalService);

  constructor() {
    this.translate.onLangChange
      .pipe(takeUntilDestroyed())
      .subscribe((params: LangChangeEvent) => {
        this.lang = params.lang;
      });

    effect(() => {
      const item = this.purchaseService.transactionUpdates();
      this.status = item.status;
    });
  }

  ngAfterViewInit(): void {
    // if (isPlatformBrowser(this._platformId)) {
    //   const instance = window.initRingsAnimation(this.ringsCanvas.nativeElement);
    //   this.ringsAnimationDestroy = instance?.destroy;

    //   // Initialize the inactivity and exit intent trackers (browser only)
    //   this.setupInactivityTimer();
    //   this.setupExitIntent();
    // }
  }

  private setupInactivityTimer(): void {
    const userActivity$ = merge(
      fromEvent(document, 'mousemove'),
      fromEvent(document, 'keydown'),
      fromEvent(document, 'click'),
      fromEvent(document, 'scroll')
    );

    userActivity$.pipe(
      startWith(null), // Starts the 25s timer instantly on load
      switchMap(() => timer(25000)), // Resets timer on every activity
      takeUntil(this.stopListeners$)
    ).subscribe(() => {
      this.triggerPopup();
    });
  }

  private setupExitIntent(): void {
    fromEvent<MouseEvent>(document, 'mouseleave').pipe(
      filter(e => e.clientY <= 0), // Only trigger if mouse moves up towards tabs/URL bar
      takeUntil(this.stopListeners$)
    ).subscribe(() => {
      this.triggerPopup();
    });
  }

  private triggerPopup(): void {
    if (!this.popupShown && this.status === SwapStatus.not_started) {
      this.popupShown = true;
      this.exitIntentPopup();

      // Immediately stop listening to mouse/keyboard events to save resources
      this.stopListeners$.next();
    }
  }

  private loadScript(src: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${src}"]`);
      if (existing) { resolve(); return; }
      const script = document.createElement('script');
      script.src = src;
      script.onload = () => resolve();
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  launchConsole(): void {
    this.eventService.showBuyWidget('hero');
  }

  openVideoModal(): void {
    this.modalService.show(VideoModalComponent, {
      class: 'modal-xl modal-dialog-centered video-modal-wrapper',
      backdrop: true,
      ignoreBackdropClick: false,
    });
  }

  trackDocumentClick(docName: string, url: string): void {
    // this.pixelService.trackPageClick(url, docName);
  }

  exitIntentPopup() {
    this.modalRef = this.modalService.show(ExitIntentComponent, {
      class: 'modal-sm modal-dialog-centered exit-intent-modal',
      backdrop: 'static',
      ignoreBackdropClick: false,
    });
  }

  ngOnDestroy(): void {
    // Clean up event listeners if user navigates away before popup triggers
    this.stopListeners$.next();
    this.stopListeners$.complete();

    // Clean up the rings animation
    if (this.ringsAnimationDestroy) {
      this.ringsAnimationDestroy();
    }
  }
}